import { useState, useEffect } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import { AdminGuard } from "../components/admin/AdminGuard";
import {
  Search,
  Filter,
  Plus,
  Download,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Users,
  CheckCircle,
  UserPlus,
  UserMinus,
  Edit,
  Eye,
  CreditCard,
  Trash2,
  X,
  DollarSign,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Building,
  RefreshCw,
  Shield,
  Globe,
  Link,
  Settings,
  Activity,
  Zap,
  Ban,
  UserCheck,
  Clock,
  Wifi,
  WifiOff,
  LogOut,
  ShoppingBag,
  Star,
  MessageCircle,
  Camera,
  Share2
} from "lucide-react";

import apiClient from "../services/api";
import BrandLogo from "../components/BrandLogo";
import { useBrandLogos } from "../hooks/useBrandLogos";

interface Customer {
  _id: string;
  firstName?: string;
  lastName?: string;
  Email: string;
  BizamplyId: string;
  createdAt: string;
  lastLogin?: string;
  lastLoginIP?: string;
  loginHistory?: Array<{
    timestamp: string;
    ipAddress: string;
    userAgent: string;
    success: boolean;
    loginMethod: 'google' | 'email' | 'admin';
  }>;
  status: 'online' | 'active' | 'inactive';
  isOnline: boolean;
  totalCredits: number;
  usedCredits: number;
  availableCredits: number;
  connectedPlatforms: Array<{
    name: string;
    count: number;
    data: any[];
  }>;
  primaryBrand?: {
    brandId: string;
    brandName: string;
  };
  totalConnections: number;
  transactionStats: {
    totalCreditsUsed: number;
    totalCreditsAdded: number;
    totalTransactions: number;
    lastTransaction?: string;
  };
}

interface CustomerData {
  customers: Customer[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export default function CustomersPage() {
  const { fetchBulkLogos } = useBrandLogos();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isLoading, setIsLoading] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [customerConnections, setCustomerConnections] = useState<any>(null);

  // Edit form states
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    Email: '',
    accountType: 'free'
  });

  // Credit form states
  const [creditForm, setCreditForm] = useState({
    amount: '',
    description: ''
  });

  // Credit settings state with proper initialization
  const [creditSettings, setCreditSettings] = useState({
    freeCredits: 100,
    generateCredits: 2,
    sendCredits: 1,
    whatsappExtraCredits: 1,
    lastUpdated: null as string | null,
    updatedBy: null as string | null,
    version: 1
  });

  // Keep a backup of initial settings to prevent data loss
  const [initialCreditSettings, setInitialCreditSettings] = useState({
    freeCredits: 100,
    generateCredits: 2,
    sendCredits: 1,
    whatsappExtraCredits: 1,
    lastUpdated: null as string | null,
    updatedBy: null as string | null,
    version: 1
  });
  const [showCreditSettingsModal, setShowCreditSettingsModal] = useState(false);
  const [creditSettingsLoading, setCreditSettingsLoading] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addCustomerForm, setAddCustomerForm] = useState({
    firstName: '',
    lastName: '',
    Email: '',
    initialCredits: 100
  });

  // Fetch customers from backend
  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        search: searchQuery,
        status: selectedStatus,
        sortBy,
        sortOrder
      });

      const response = await apiClient.get(`/backend/api/admin/customers?${params}`);

      if (response.data.success) {
        setCustomerData(response.data.data);

        // Extract brand names and fetch logos
        const brands = response.data.data.customers
          .filter((customer: Customer) => customer.primaryBrand)
          .map((customer: Customer) => ({
            name: customer.primaryBrand!.brandName,
            domain: undefined // We don't have domain info, so let the fetcher try to find it
          }));

        if (brands.length > 0) {
          fetchBulkLogos(brands).catch(console.error);
        }
      } else {
        setError(response.data.error || 'Failed to fetch customers');
      }
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      setError(error.response?.data?.error || 'Failed to fetch customers');
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch customers when filters change
  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchQuery, selectedStatus, sortBy, sortOrder]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, selectedStatus]);

  // Modal functions
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      Email: customer.Email,
      accountType: 'free' // You can add this field to the customer model
    });
    setShowEditModal(true);
  };

  const handleViewCustomer = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowViewModal(true);

    // Fetch customer transactions
    try {
      const response = await apiClient.get(`/backend/api/admin/customers/${customer._id}/transactions`);
      if (response.data.success) {
        setCustomerTransactions(response.data.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching customer transactions:', error);
      setCustomerTransactions([]); // Set empty array on error
    }
  };

  const handleAddCredits = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCreditForm({ amount: '', description: '' });
    setShowCreditModal(true);
  };

  const handleViewTransactions = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowTransactionModal(true);

    try {
      const response = await apiClient.get(`/backend/api/admin/customers/${customer._id}/transactions`);
      if (response.data.success) {
        setCustomerTransactions(response.data.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching customer transactions:', error);
      setCustomerTransactions([]); // Set empty array on error
    }
  };

  // Form submission functions
  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    try {
      const response = await apiClient.put(`/backend/api/admin/customers/${selectedCustomer._id}`, editForm);
      if (response.data.success) {
        setShowEditModal(false);
        fetchCustomers(); // Refresh the list
        alert('Customer updated successfully');
      }
    } catch (error: any) {
      console.error('Error updating customer:', error);
      alert(error.response?.data?.error || 'Failed to update customer');
    }
  };

  const handleAddCreditsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomer || !creditForm.amount) return;

    try {
      // Add validation to prevent negative amounts
      const amount = parseInt(creditForm.amount);
      if (amount <= 0) {
        alert('Credit amount must be positive');
        return;
      }

      // Generate idempotency key to prevent duplicate credit additions
      const idempotencyKey = `add_credits_${selectedCustomer._id}_${Date.now()}`;

      const response = await apiClient.post(`/backend/api/admin/customers/${selectedCustomer._id}/credits`, {
        amount: amount,
        description: creditForm.description || 'Admin credit adjustment',
        idempotencyKey
      }, {
        headers: {
          'X-Idempotency-Key': idempotencyKey
        }
      });

      if (response.data.success) {
        setShowCreditModal(false);
        fetchCustomers(); // Refresh the list
        alert(`Added ${creditForm.amount} credits successfully`);
      }
    } catch (error: any) {
      console.error('Error adding credits:', error);
      alert(error.response?.data?.error || 'Failed to add credits');
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (!confirm('Are you sure you want to deactivate this customer?')) return;

    try {
      const response = await apiClient.delete(`/api/admin/customers/${customerId}`);
      if (response.data.success) {
        fetchCustomers(); // Refresh the list
        alert('Customer deactivated successfully');
      }
    } catch (error: any) {
      console.error('Error deactivating customer:', error);
      alert(error.response?.data?.error || 'Failed to deactivate customer');
    }
  };



  const handleBanCustomer = async (customer: Customer) => {
    if (!confirm(`Are you sure you want to ban ${customer.Email}? This will prevent them from accessing the platform.`)) return;

    try {
      const response = await apiClient.post(`/api/admin/customers/${customer._id}/ban`);
      if (response.data.success) {
        fetchCustomers();
        alert('Customer banned successfully');
      }
    } catch (error: any) {
      console.error('Error banning customer:', error);
      alert(error.response?.data?.error || 'Failed to ban customer');
    }
  };

  const handleActivateCustomer = async (customer: Customer) => {
    try {
      const response = await apiClient.post(`/backend/api/admin/customers/${customer._id}/activate`);
      if (response.data.success) {
        fetchCustomers();
        alert('Customer activated successfully');
      }
    } catch (error: any) {
      console.error('Error activating customer:', error);
      alert(error.response?.data?.error || 'Failed to activate customer');
    }
  };

  const handleViewActivity = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowActivityModal(true);
  };

  const handleManageConnections = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowConnectionsModal(true);
    setCustomerConnections(null); // Reset to show loading

    // Fetch detailed connection data
    try {
      const response = await apiClient.get(`/backend/api/admin/customers/${customer._id}/connections`);
      if (response.data.success) {
        setCustomerConnections(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      setCustomerConnections(null);
    }
  };

  const handleRemoveConnection = async (customerId: string, platform: string) => {
    const platformName = platform === 'judgeme' ? 'Judge.me' : platform.charAt(0).toUpperCase() + platform.slice(1);

    if (!confirm(`Are you sure you want to remove the ${platformName} connection? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/backend/api/admin/customers/${customerId}/connections/${platform}`);
      if (response.data.success) {
        // Refresh customer data
        fetchCustomers();

        // If viewing customer details, refresh that too
        if (selectedCustomer && selectedCustomer._id === customerId) {
          const customerResponse = await apiClient.get(`/backend/api/admin/customers/${customerId}`);
          if (customerResponse.data.success) {
            setSelectedCustomer(customerResponse.data.data.customer);
          }
        }

        // If connections modal is open, refresh connections data
        if (showConnectionsModal && selectedCustomer && selectedCustomer._id === customerId) {
          try {
            const connectionsResponse = await apiClient.get(`/backend/api/admin/customers/${customerId}/connections`);
            if (connectionsResponse.data.success) {
              setCustomerConnections(connectionsResponse.data.data);
            }
          } catch (error) {
            console.error('Error refreshing connections:', error);
          }
        }

        alert(`${platformName} connection removed successfully`);
      }
    } catch (error: any) {
      console.error('Error removing connection:', error);
      alert(error.response?.data?.error || 'Failed to remove connection');
    }
  };



  // Credit settings functions
  const loadCreditSettings = async () => {
    try {
      setCreditSettingsLoading(true);
      const response = await apiClient.get('/backend/api/credits/settings');

      if (response.data.success) {
        const settings = response.data.data;
        // Ensure all values are properly defined to prevent controlled/uncontrolled input issues
        const safeSettings = {
          freeCredits: settings.freeCredits ?? 100,
          generateCredits: settings.generateCredits ?? 2,
          sendCredits: settings.sendCredits ?? 1,
          whatsappExtraCredits: settings.whatsappExtraCredits ?? 1,
          lastUpdated: settings.lastUpdated || null,
          updatedBy: settings.updatedBy || null,
          version: settings.version ?? 1
        };
        setCreditSettings(safeSettings);
        setInitialCreditSettings(safeSettings); // Keep backup for reset functionality
      }
    } catch (error) {
      console.error('Error loading credit settings:', error);
      // Keep default values if loading fails
    } finally {
      setCreditSettingsLoading(false);
    }
  };

  const saveCreditSettings = async () => {
    try {
      setCreditSettingsLoading(true);
      console.log('💾 Saving credit settings:', creditSettings);

      const response = await apiClient.post('/backend/api/credits/settings', creditSettings);

      if (response.data.success) {
        console.log('✅ Credit settings saved successfully:', response.data.data);
        alert('Credit settings saved successfully!');
        // Update the backup settings to match current settings
        setInitialCreditSettings(creditSettings);
        setShowCreditSettingsModal(false);
      } else {
        console.error('❌ Error saving credit settings:', response.data.error);
        alert('Error saving credit settings: ' + response.data.error);
      }
    } catch (error: any) {
      console.error('❌ Error saving credit settings:', error);
      alert('Error saving credit settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreditSettingsLoading(false);
    }
  };

  const handleCreditSettings = () => {
    setShowCreditSettingsModal(true);
    loadCreditSettings();
  };

  const resetCreditSettings = () => {
    setCreditSettings(initialCreditSettings);
  };

  const handleCloseCreditSettings = () => {
    // Reset to initial values when closing without saving
    setCreditSettings(initialCreditSettings);
    setShowCreditSettingsModal(false);
  };

  // Add customer functionality
  const handleAddCustomer = () => {
    setShowAddCustomerModal(true);
  };

  const handleAddCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiClient.post('/backend/api/admin/customers', addCustomerForm);

      if (response.data.success) {
        alert('Customer added successfully!');
        setShowAddCustomerModal(false);
        setAddCustomerForm({
          firstName: '',
          lastName: '',
          Email: '',
          initialCredits: 100
        });
        fetchCustomers(); // Refresh the customer list
      } else {
        alert('Error adding customer: ' + response.data.error);
      }
    } catch (error: any) {
      console.error('Error adding customer:', error);
      alert('Error adding customer: ' + (error.response?.data?.error || error.message));
    }
  };



  // Utility functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const exportCustomers = async () => {
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        status: selectedStatus,
        limit: '1000' // Export more records
      });

      const response = await apiClient.get(`/api/admin/customers?${params}`);

      if (response.data.success) {
        const customers = response.data.data.customers;

        // Convert to CSV
        const csvHeaders = [
          'Customer ID',
          'First Name',
          'Last Name',
          'Email',
          'Status',
          'Total Credits',
          'Used Credits',
          'Available Credits',
          'Total Transactions',
          'Join Date',
          'Last Login'
        ];

        const csvRows = customers.map((customer: Customer) => [
          customer.BizamplyId,
          customer.firstName || '',
          customer.lastName || '',
          customer.Email,
          customer.status,
          customer.totalCredits,
          customer.usedCredits,
          customer.availableCredits,
          customer.transactionStats.totalTransactions,
          formatDate(customer.createdAt).date,
          customer.lastLogin ? formatDate(customer.lastLogin).date : 'Never'
        ]);

        // Create CSV content
        const csvContent = [
          csvHeaders.join(','),
          ...csvRows.map((row: any[]) =>
            row.map((field: any) =>
              typeof field === 'string' && field.includes(',')
                ? `"${field.replace(/"/g, '""')}"`
                : field
            ).join(',')
          )
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `customers-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export customers. Please try again.');
    }
  };



  return (
    <AdminGuard>
      <DashboardLayout>
      <PageHeader
        title="Customer Management"
        description="Comprehensive customer management panel"
        onRefresh={fetchCustomers}
      />

      {/* Stats Cards */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mb-6">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Customers</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{customerData?.pagination.totalCount || 0}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Across all segments</div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Customers</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <UserPlus className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{customerData?.customers.filter(c => c.status === 'active').length || 0}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Currently active</div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Credits</h3>
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <CreditCard className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">{customerData?.customers.reduce((sum, c) => sum + c.totalCredits, 0) || 0}</span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Across all customers</div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative w-full sm:w-64 md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search customers..."
            className="pl-10 pr-4 py-2 w-full rounded-lg bg-white dark:bg-gray-900 text-sm transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700 border border-gray-200 dark:border-gray-800"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200">
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>

          <button
            onClick={exportCustomers}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            disabled={isLoading}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={fetchCustomers}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            onClick={handleCreditSettings}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Credit Settings</span>
          </button>

          <button
            onClick={handleAddCustomer}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-white transition-all duration-200"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Customer</span>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 p-8 text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400">Loading customers...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading customers</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchCustomers}
                  className="bg-red-100 dark:bg-red-900/30 px-3 py-2 rounded-md text-sm font-medium text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-900/50"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customers Table */}
      {!isLoading && !error && customerData && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status & Activity</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Credits</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Activity</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Join Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customerData.customers.map((customer) => (
                  <tr key={customer._id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium">
                          {customer.firstName || customer.lastName
                            ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                            : customer.Email.split('@')[0]
                          }
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{customer.Email}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">ID: {customer.BizamplyId}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === 'online' || customer.isOnline
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : customer.status === 'active'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {customer.isOnline && <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>}
                            {customer.isOnline ? 'Online' : customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          {customer.isOnline ? (
                            <><Wifi className="h-3 w-3" /> Active now</>
                          ) : customer.lastLogin ? (
                            <><Clock className="h-3 w-3" /> {formatDate(customer.lastLogin).date}</>
                          ) : (
                            <><WifiOff className="h-3 w-3" /> Never logged in</>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <div className="font-medium">{customer.availableCredits} available</div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {customer.totalCredits} total / {customer.usedCredits} used
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <div className="font-medium flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          {customer.transactionStats.totalTransactions} transactions
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {customer.transactionStats.totalCreditsUsed} credits used
                        </div>
                        {customer.transactionStats.lastTransaction && (
                          <div className="text-xs text-gray-400">
                            Last: {formatDate(customer.transactionStats.lastTransaction).date}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {formatDate(customer.createdAt).date}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewCustomer(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditCustomer(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                          title="Edit Customer"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleAddCredits(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                          title="Add Credits"
                        >
                          <CreditCard className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewTransactions(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                          title="View Transactions"
                        >
                          <DollarSign className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleManageConnections(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
                          title="Manage Connections"
                        >
                          <Link className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleViewActivity(customer)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          title="View Activity Log"
                        >
                          <Activity className="h-4 w-4" />
                        </button>
                        {customer.status === 'active' ? (
                          <button
                            onClick={() => handleBanCustomer(customer)}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Ban Customer"
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateCustomer(customer)}
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                            title="Activate Customer"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCustomer(customer._id)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Deactivate Customer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {customerData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing <span className="font-medium">{(customerData.pagination.currentPage - 1) * customerData.pagination.limit + 1}</span> to <span className="font-medium">{Math.min(customerData.pagination.currentPage * customerData.pagination.limit, customerData.pagination.totalCount)}</span> of <span className="font-medium">{customerData.pagination.totalCount}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-800 ${!customerData.pagination.hasPrev ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!customerData.pagination.hasPrev}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {Array.from({ length: Math.min(customerData.pagination.totalPages, 5) }, (_, i) => {
                  const startPage = Math.max(1, customerData.pagination.currentPage - 2);
                  const page = startPage + i;
                  if (page > customerData.pagination.totalPages) return null;

                  return (
                    <button
                      key={page}
                      className={`px-3 py-1 rounded-lg ${customerData.pagination.currentPage === page ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-800'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className={`p-2 rounded-lg border border-gray-200 dark:border-gray-800 ${!customerData.pagination.hasNext ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, customerData.pagination.totalPages))}
                  disabled={!customerData.pagination.hasNext}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold">Edit Customer</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateCustomer} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.Email}
                    onChange={(e) => setEditForm({ ...editForm, Email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Credits Modal */}
      {showCreditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold">Add Credits</h3>
              <button
                onClick={() => setShowCreditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddCreditsSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer
                  </label>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedCustomer.firstName || selectedCustomer.lastName
                      ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                      : selectedCustomer.Email.split('@')[0]
                    } ({selectedCustomer.Email})
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Current: {selectedCustomer.availableCredits} available / {selectedCustomer.totalCredits} total
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Credits to Add
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={creditForm.description}
                    onChange={(e) => setCreditForm({ ...creditForm, description: e.target.value })}
                    placeholder="Admin credit adjustment"
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreditModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add Credits
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Customer Details Modal */}
      {showViewModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold">Customer Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Customer Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">
                            {selectedCustomer.firstName || selectedCustomer.lastName
                              ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                              : selectedCustomer.Email.split('@')[0]
                            }
                          </div>
                          <div className="text-xs text-gray-500">{selectedCustomer.Email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Building className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">Customer ID</div>
                          <div className="text-xs text-gray-500">{selectedCustomer.BizamplyId}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">Join Date</div>
                          <div className="text-xs text-gray-500">{formatDate(selectedCustomer.createdAt).date}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium">Last Login</div>
                          <div className="text-xs text-gray-500">
                            {selectedCustomer.lastLogin ? formatDate(selectedCustomer.lastLogin).date : 'Never'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 rounded-full ${selectedCustomer.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                        <div>
                          <div className="text-sm font-medium">Status</div>
                          <div className="text-xs text-gray-500 capitalize">{selectedCustomer.status}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credit Information */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Credit Information</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{selectedCustomer.availableCredits}</div>
                        <div className="text-xs text-gray-500">Available</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{selectedCustomer.totalCredits}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{selectedCustomer.usedCredits}</div>
                        <div className="text-xs text-gray-500">Used</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Usage</span>
                        <span>{Math.round((selectedCustomer.usedCredits / selectedCustomer.totalCredits) * 100) || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min((selectedCustomer.usedCredits / selectedCustomer.totalCredits) * 100, 100) || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Stats */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Transaction Statistics</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Total Transactions</span>
                        <span className="text-sm font-medium">{selectedCustomer.transactionStats.totalTransactions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Credits Used</span>
                        <span className="text-sm font-medium">{selectedCustomer.transactionStats.totalCreditsUsed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Credits Added</span>
                        <span className="text-sm font-medium">{selectedCustomer.transactionStats.totalCreditsAdded}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Last Transaction</span>
                        <span className="text-sm font-medium">
                          {selectedCustomer.transactionStats.lastTransaction
                            ? formatDate(selectedCustomer.transactionStats.lastTransaction).date
                            : 'None'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Connected Platforms */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                      <Link className="h-4 w-4" />
                      Connected Platforms ({selectedCustomer.totalConnections || 0})
                    </h4>
                    <div className="space-y-3">
                      {selectedCustomer.connectedPlatforms && selectedCustomer.connectedPlatforms.length > 0 ? (
                        selectedCustomer.connectedPlatforms.map((platform: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center gap-3">
                              <BrandLogo
                                platformName={platform.name.toLowerCase().replace('.', '')}
                                size="md"
                                className="rounded"
                                fallbackText={platform.name}
                              />
                              <div>
                                <div className="text-sm font-medium">{platform.name}</div>
                                <div className="text-xs text-gray-500">{platform.count} connection{platform.count !== 1 ? 's' : ''}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                Connected
                              </span>
                              {platform.name !== 'Shopify' && (
                                <button
                                  onClick={() => handleRemoveConnection(selectedCustomer._id, platform.name.toLowerCase().replace('.', ''))}
                                  className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                                  title={`Remove ${platform.name} connection`}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <Link className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No platforms connected</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recent Transactions</h4>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleViewTransactions(selectedCustomer);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {customerTransactions.slice(0, 10).map((transaction: any, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              transaction.status === 'success'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : transaction.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {transaction.status}
                            </span>
                            <span className="text-xs text-gray-500">{transaction.platform}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(transaction.createdAt).date}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-sm">
                            {transaction.creditsUsed > 0 && (
                              <span className="text-red-600">-{transaction.creditsUsed} credits</span>
                            )}
                            {transaction.creditsAdded > 0 && (
                              <span className="text-green-600">+{transaction.creditsAdded} credits</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">{transaction.type}</div>
                        </div>
                        {transaction.description && (
                          <div className="text-xs text-gray-500 mt-1">{transaction.description}</div>
                        )}
                      </div>
                    ))}
                    {customerTransactions.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No transactions found
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditCustomer(selectedCustomer);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Edit Customer
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleAddCredits(selectedCustomer);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <CreditCard className="h-4 w-4" />
                  Add Credits
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleViewTransactions(selectedCustomer);
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  View Transactions
                </button>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History Modal */}
      {showTransactionModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h3 className="text-lg font-semibold">Transaction History</h3>
                <p className="text-sm text-gray-500">
                  {selectedCustomer.firstName || selectedCustomer.lastName
                    ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                    : selectedCustomer.Email.split('@')[0]
                  } ({selectedCustomer.Email})
                </p>
              </div>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Transaction Filters */}
              <div className="flex gap-4 mb-6">
                <select className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                  <option value="all">All Types</option>
                  <option value="usage">Usage</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="refund">Refund</option>
                </select>
                <select className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                  <option value="all">All Status</option>
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <select className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
                  <option value="all">All Platforms</option>
                  <option value="shopify">Shopify</option>
                  <option value="judgeme">Judge.me</option>
                  <option value="yotpo">Yotpo</option>
                  <option value="instagram">Instagram</option>
                  <option value="system">System</option>
                </select>
              </div>

              {/* Transactions Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Platform</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Credits</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Description</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerTransactions.map((transaction: any, index: number) => (
                        <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="py-3 px-4 text-sm">
                            <div>{formatDate(transaction.createdAt).date}</div>
                            <div className="text-xs text-gray-500">{formatDate(transaction.createdAt).time}</div>
                          </td>
                          <td className="py-3 px-4 text-sm capitalize">{transaction.type}</td>
                          <td className="py-3 px-4 text-sm capitalize">{transaction.platform}</td>
                          <td className="py-3 px-4 text-sm">
                            {transaction.creditsUsed > 0 && (
                              <span className="text-red-600 font-medium">-{transaction.creditsUsed}</span>
                            )}
                            {transaction.creditsAdded > 0 && (
                              <span className="text-green-600 font-medium">+{transaction.creditsAdded}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              transaction.status === 'success'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : transaction.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                            {transaction.description || '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => {
                                if (transaction.status === 'success' && transaction.creditsUsed > 0) {
                                  if (confirm('Are you sure you want to refund this transaction?')) {
                                    // Handle refund logic here
                                    alert('Refund functionality would be implemented here');
                                  }
                                }
                              }}
                              className={`text-xs px-2 py-1 rounded ${
                                transaction.status === 'success' && transaction.creditsUsed > 0
                                  ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              disabled={!(transaction.status === 'success' && transaction.creditsUsed > 0)}
                            >
                              {transaction.status === 'success' && transaction.creditsUsed > 0 ? 'Refund' : '-'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {customerTransactions.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No transactions found for this customer</p>
                  </div>
                )}
              </div>

              {/* Transaction Summary */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{customerTransactions.length}</div>
                  <div className="text-xs text-gray-500">Total Transactions</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {customerTransactions.reduce((sum: number, t: any) => sum + (t.creditsAdded || 0), 0)}
                  </div>
                  <div className="text-xs text-gray-500">Credits Added</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {customerTransactions.reduce((sum: number, t: any) => sum + (t.creditsUsed || 0), 0)}
                  </div>
                  <div className="text-xs text-gray-500">Credits Used</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {customerTransactions.filter((t: any) => t.status === 'success').length}
                  </div>
                  <div className="text-xs text-gray-500">Successful</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {showActivityModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Log - {selectedCustomer.Email}
              </h3>
              <button
                onClick={() => setShowActivityModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Current Status */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Current Status
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Status</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedCustomer.isOnline
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : selectedCustomer.status === 'active'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                      }`}>
                        {selectedCustomer.isOnline && <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>}
                        {selectedCustomer.isOnline ? 'Online' : selectedCustomer.status.charAt(0).toUpperCase() + selectedCustomer.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Last Login</span>
                      <span className="text-sm font-medium">
                        {selectedCustomer.lastLogin ? formatDate(selectedCustomer.lastLogin).date : 'Never'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Last IP Address</span>
                      <span className="text-sm font-medium font-mono">
                        {(selectedCustomer as any).lastLoginIP || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Login History */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Recent Login History
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(selectedCustomer as any).loginHistory && (selectedCustomer as any).loginHistory.length > 0 ? (
                      (selectedCustomer as any).loginHistory.slice(0, 10).map((login: any, index: number) => (
                        <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                login.success
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {login.success ? 'Success' : 'Failed'}
                              </span>
                              <span className="text-xs text-gray-500 capitalize">{login.loginMethod}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(login.timestamp).date}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
                              IP: {login.ipAddress}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(login.timestamp).time}
                            </span>
                          </div>
                          {login.userAgent && (
                            <div className="mt-2 text-xs text-gray-500 truncate">
                              {login.userAgent.substring(0, 80)}...
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No login history available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Credit Settings Modal */}
      {showCreditSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold">Credit Settings</h3>
              <button
                onClick={handleCloseCreditSettings}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-md font-medium mb-4">Credit Configuration</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Free Credits for New Users
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={creditSettings.freeCredits}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                          setCreditSettings(prev => ({ ...prev, freeCredits: value }));
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-sm border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Credits given to new users when they sign up</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Generate Response Credits
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={creditSettings.generateCredits}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                          setCreditSettings(prev => ({ ...prev, generateCredits: value }));
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-sm border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Credits deducted when generating AI responses</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Send Response Credits
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={creditSettings.sendCredits}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                          setCreditSettings(prev => ({ ...prev, sendCredits: value }));
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-sm border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Credits deducted when sending responses to platforms</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        WhatsApp Extra Credits
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={creditSettings.whatsappExtraCredits}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                          setCreditSettings(prev => ({ ...prev, whatsappExtraCredits: value }));
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-sm border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Extra credits for WhatsApp sending (added to send credits)</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                  <h4 className="text-md font-medium mb-4">Credit Summary</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Generate Response:</span>
                      <span className="font-medium">{creditSettings.generateCredits || 0} credits</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Send to Platform:</span>
                      <span className="font-medium">{creditSettings.sendCredits || 0} credits</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Send via WhatsApp:</span>
                      <span className="font-medium">{(creditSettings.sendCredits || 0) + (creditSettings.whatsappExtraCredits || 0)} credits</span>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-sm">Total (Generate + Send to Platform):</span>
                        <span className="text-lg">{(creditSettings.generateCredits || 0) + (creditSettings.sendCredits || 0)} credits</span>
                      </div>
                      <div className="flex justify-between items-center font-semibold mt-2">
                        <span className="text-sm">Total (Generate + Send via WhatsApp):</span>
                        <span className="text-lg">{(creditSettings.generateCredits || 0) + (creditSettings.sendCredits || 0) + (creditSettings.whatsappExtraCredits || 0)} credits</span>
                      </div>
                    </div>

                    {/* Show last updated info */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <div>Last updated: {creditSettings.lastUpdated ? new Date(creditSettings.lastUpdated).toLocaleString() : 'Never'}</div>
                        <div>Updated by: {creditSettings.updatedBy || 'System'}</div>
                        <div>Version: {creditSettings.version || 1}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseCreditSettings}
                  className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={resetCreditSettings}
                  className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  Reset
                </button>
                <button
                  onClick={saveCreditSettings}
                  disabled={creditSettingsLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creditSettingsLoading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Settings className="h-4 w-4" />
                  )}
                  {creditSettingsLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold">Add New Customer</h3>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddCustomerSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={addCustomerForm.firstName}
                    onChange={(e) => setAddCustomerForm({ ...addCustomerForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={addCustomerForm.lastName}
                    onChange={(e) => setAddCustomerForm({ ...addCustomerForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={addCustomerForm.Email}
                    onChange={(e) => setAddCustomerForm({ ...addCustomerForm, Email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Initial Credits
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={addCustomerForm.initialCredits}
                    onChange={(e) => setAddCustomerForm({ ...addCustomerForm, initialCredits: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Connections Management Modal */}
      {showConnectionsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Manage Connections
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedCustomer.firstName || selectedCustomer.lastName
                    ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                    : selectedCustomer.Email.split('@')[0]
                  } ({selectedCustomer.Email})
                </p>
              </div>
              <button
                onClick={() => setShowConnectionsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {customerConnections ? (
                  Object.entries(customerConnections).map(([platform, connection]: [string, any]) => (
                    <div key={platform} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <BrandLogo
                            platformName={connection.name.toLowerCase().replace('.', '')}
                            size="lg"
                            className="rounded"
                            fallbackText={connection.name}
                          />
                          <div>
                            <h4 className="font-medium">{connection.name}</h4>
                            <p className="text-xs text-gray-500">{connection.description}</p>
                            <p className="text-xs text-gray-400">{connection.type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {connection.connected ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Connected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                              <X className="h-3 w-3 mr-1" />
                              Not Connected
                            </span>
                          )}
                        </div>
                      </div>

                      {connection.connected && (
                        <div className="space-y-3">
                          <div className="text-sm">
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                              {connection.data.length} connection{connection.data.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {connection.data.slice(0, 3).map((item: any, index: number) => (
                            <div key={index} className="bg-white dark:bg-gray-700 rounded p-3 text-xs border border-gray-100 dark:border-gray-600">
                              <div className="flex items-start gap-3">
                                {/* Brand Logo */}
                                {(item.shop || item.PageName || item.BrandName) && (
                                  <BrandLogo
                                    brandName={item.BrandName || item.shop || item.PageName}
                                    domain={item.shop ? `${item.shop}.myshopify.com` : undefined}
                                    size="sm"
                                    className="rounded flex-shrink-0"
                                    fallbackText={item.BrandName || item.shop || item.PageName}
                                  />
                                )}

                                {/* Connection Details */}
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  {item.BrandId && (
                                    <div>
                                      <span className="font-medium text-gray-700 dark:text-gray-300">Brand ID:</span>
                                      <div className="text-gray-600 dark:text-gray-400">{item.BrandId}</div>
                                    </div>
                                  )}
                                  {item.shop && (
                                    <div>
                                      <span className="font-medium text-gray-700 dark:text-gray-300">Shop:</span>
                                      <div className="text-gray-600 dark:text-gray-400">{item.shop}</div>
                                    </div>
                                  )}
                                  {item.PageName && (
                                    <div>
                                      <span className="font-medium text-gray-700 dark:text-gray-300">Page:</span>
                                      <div className="text-gray-600 dark:text-gray-400">{item.PageName}</div>
                                    </div>
                                  )}
                                  {item.Username && (
                                    <div>
                                      <span className="font-medium text-gray-700 dark:text-gray-300">Username:</span>
                                      <div className="text-gray-600 dark:text-gray-400">@{item.Username}</div>
                                    </div>
                                  )}
                                  {item.BrandName && (
                                    <div className="col-span-2">
                                      <span className="font-medium text-gray-700 dark:text-gray-300">Brand:</span>
                                      <div className="text-gray-600 dark:text-gray-400">{item.BrandName}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          {connection.data.length > 3 && (
                            <div className="text-xs text-gray-500 text-center">
                              +{connection.data.length - 3} more connection{connection.data.length - 3 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                        {connection.connected && connection.canRemove && (
                          <button
                            onClick={() => {
                              setShowConnectionsModal(false);
                              handleRemoveConnection(selectedCustomer._id, platform);
                            }}
                            className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                          >
                            <Trash2 className="h-3 w-3" />
                            Remove
                          </button>
                        )}
                        {!connection.connected && (
                          <span className="px-3 py-1.5 text-xs bg-gray-100 text-gray-500 rounded">
                            Not Available
                          </span>
                        )}
                        {platform === 'shopify' && connection.connected && (
                          <span className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded">
                            Protected
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <RefreshCw className="h-8 w-8 mx-auto mb-2 animate-spin" />
                    <p>Loading connections...</p>
                  </div>
                )}
              </div>

              {customerConnections && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Connection Management</h4>
                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                          • Shopify connections cannot be removed as they are core to the platform<br/>
                          • Removing connections will permanently delete all associated data<br/>
                          • Users will need to reconnect manually to restore functionality
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </DashboardLayout>
    </AdminGuard>
  );
}